import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Product, Category, Location, ProductReviewsSummary } from "../interfaces/products.interface";
import type { DraftListing, Listing, MyListingsProduct, MyListingsStats, MyListingsPagination } from "../../myListings/interfaces/myListings.interface";
import {
  fetchProducts,
  fetchProductById,
  fetchProductBySlug,
  fetchProductReviews,
  postProductReview,
  fetchSellerProducts,
  fetchRelatedProducts,
  createProduct,
  updateProduct,
  updateProductStatus,
  deleteProduct,
  fetchUserProducts,
  searchProducts,
  fetchCategories,
  fetchLocations,
  fetchMyListings,
  fetchDrafts,
} from "../thunks/products.thunk";
import type { ProductsState, CategoryPaginationState } from "../interfaces/products.interface";

const initialMyListingsStats: MyListingsStats = {
  total: 0,
  drafts: 0,
  published: 0,
  sold: 0,
};

const initialMyListingsPagination: MyListingsPagination = {
  page: 1,
  totalPages: 1,
  total: 0,
  limit: 10,
};

const initialState: ProductsState = {
  products: [],
  currentProduct: null,
  userProducts: [],
  categoriesItems: [],
  locations: [],
  searchResults: [],
  searchTotal: 0,
  searchPage: 1,
  productReviews: null,
  sellerProducts: [],
  relatedProducts: [],
  isLoading: false,
  isLoadingReviews: false,
  isLoadingSellerProducts: false,
  isLoadingRelated: false,
  error: null,
  pagination: {
    page: 1,
    totalPages: 1,
    total: 0,
  },
  draftListings: [],
  draftCount: 0,
  stats: initialMyListingsStats,
  listings: [],
  myListingsProducts: [],
  myListingsPagination: initialMyListingsPagination,
  isLoadingMyListings: false,
  draftsLoading: false,
  myListingsError: null,
  categories: {},
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentProduct: (state, action: PayloadAction<Product | null>) => {
      state.currentProduct = action.payload;
    },
    clearProducts: (state) => {
      state.products = [];
      state.pagination = initialState.pagination;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchPage = 1;
    },
    clearUserProducts: (state) => {
      state.userProducts = [];
    },
    clearCurrentProductDetail: (state) => {
      state.currentProduct = null;
      state.productReviews = null;
      state.sellerProducts = [];
      state.relatedProducts = [];
    },
    // My Listings (consolidated)
    setDraftListings: (state, action: PayloadAction<DraftListing[]>) => {
      state.draftListings = action.payload;
      state.draftCount = action.payload.length;
    },
    addDraft: (state, action: PayloadAction<DraftListing>) => {
      if (!state.draftListings.some((d) => d.id === action.payload.id)) {
        state.draftListings.push(action.payload);
        state.draftCount = state.draftListings.length;
      }
    },
    removeDraft: (state, action: PayloadAction<string>) => {

      state.draftListings = state.draftListings.filter((d) => d.id !== action.payload);
      state.draftCount = state.draftListings.length;

    },
    updateProductInList: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<MyListingsProduct> }>
    ) => {
      const { id, updates } = action.payload;
      const idx = state.myListingsProducts.findIndex((p) => p.id === id);
      if (idx !== -1) state.myListingsProducts[idx] = { ...state.myListingsProducts[idx], ...updates };
      const listIdx = state.listings.findIndex((l) => l.id === id);
      if (listIdx !== -1 && (updates as any).status === "PUBLISHED") {
        state.listings[listIdx] = { ...state.listings[listIdx], status: "active" };
      }
    },
    updateListingInList: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Listing> }>
    ) => {
      const { id, updates } = action.payload;
      const idx = state.listings.findIndex((l) => l.id === id);
      if (idx !== -1) state.listings[idx] = { ...state.listings[idx], ...updates };
    },
    removeListing: (state, action: PayloadAction<string>) => {
      const id = action.payload;
      state.myListingsProducts = state.myListingsProducts.filter((p) => p.id !== id);
      state.listings = state.listings.filter((l) => l.id !== id);
    },
    setMyListingsPage: (state, action: PayloadAction<number>) => {
      state.myListingsPagination.page = action.payload;
    },
    clearMyListings: (state) => {
      state.myListingsProducts = [];
      state.listings = [];
      state.draftListings = [];
      state.draftCount = 0;
      state.stats = { ...initialMyListingsStats };
      state.myListingsPagination = { ...initialMyListingsPagination };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Products (Category-scoped)
      .addCase(fetchProducts.pending, (state, action) => {
        const category = action.meta.arg?.category || "All";
        if (!state.categories[category]) {
          state.categories[category] = {
            items: [],
            pages: {},
            page: 1,
            totalPages: 1,
            loading: true,
            initialized: false,
          };
        } else {
          state.categories[category].loading = true;
        }
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        const { items, page, totalPages, category } = action.payload;
        const targetCategory = category || "All";

        if (!state.categories[targetCategory]) {
          state.categories[targetCategory] = {
            items: [],
            pages: {},
            page: 1,
            totalPages: 1,
            loading: false,
            initialized: false,
          };
        }

        const catState = state.categories[targetCategory];
        catState.loading = false;
        catState.initialized = true;
        catState.page = page;
        catState.totalPages = totalPages;

        if (!catState.pages) catState.pages = {};
        catState.pages[page] = items;

        if (action.payload.append) {
          // Prevent duplicates
          const newItems = items.filter(
            (newItem) => !state.categories[targetCategory].items.some((existingItem) => existingItem.id === newItem.id)
          );
          state.categories[targetCategory].items.push(...newItems);
        } else {
          // Keep flat array intact with all cached pages so Row views (infinite scroll) aren't destroyed
          const allCachedItems: any[] = [];
          Object.keys(catState.pages)
            .sort((a, b) => Number(a) - Number(b))
            .forEach((pageNum) => {
              catState.pages![Number(pageNum)].forEach((item) => {
                if (!allCachedItems.some((existing) => existing.id === item.id)) {
                  allCachedItems.push(item);
                }
              });
            });
          state.categories[targetCategory].items = allCachedItems;
        }

        // Keep legacy flat state in sync for backward compatibility if needed, 
        // but primarily we use the category-scoped state now.
        if (targetCategory === "All") {
          state.products = items;
          state.pagination = {
            page: action.payload.page,
            totalPages: action.payload.totalPages,
            total: action.payload.total,
          };
        }

        state.isLoading = false;
        state.error = null;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        const category = action.meta.arg?.category || "All";
        if (state.categories[category]) {
          state.categories[category].loading = false;
        }
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch Product By ID
      .addCase(fetchProductById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Create Product
      .addCase(createProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        state.products.unshift(action.payload);
        state.userProducts.unshift(action.payload);
        state.error = null;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update Product
      .addCase(updateProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.isLoading = false;
        const updatedProduct = action.payload;

        // Update in products list
        const productIndex = state.products.findIndex(
          (p) => p.id === updatedProduct.id,
        );
        if (productIndex !== -1) {
          state.products[productIndex] = updatedProduct;
        }

        // Update in user products list
        const userProductIndex = state.userProducts.findIndex(
          (p) => p.id === updatedProduct.id,
        );
        if (userProductIndex !== -1) {
          state.userProducts[userProductIndex] = updatedProduct;
        }

        // Update current product if it's the same
        if (state.currentProduct?.id === updatedProduct.id) {
          state.currentProduct = updatedProduct;
        }

        state.error = null;
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Update Product Status (e.g. repost)
      .addCase(updateProductStatus.fulfilled, (state, action) => {
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
      })

      // Delete Product
      .addCase(deleteProduct.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload) {
          const deletedId = action.payload.id;

          // Remove from products list
          state.products = state.products.filter((p) => p.id !== deletedId);

          // Remove from user products list
          state.userProducts = state.userProducts.filter(
            (p) => p.id !== deletedId,
          );

          // Clear current product if it's the deleted one
          if (state.currentProduct?.id === deletedId) {
            state.currentProduct = null;
          }
        }

        state.error = null;
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch User Products
      .addCase(fetchUserProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userProducts = action.payload;
        state.error = null;
      })
      .addCase(fetchUserProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Search Products
      .addCase(searchProducts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchProducts.fulfilled, (state, action) => {
        state.isLoading = false;
        const incoming = action.payload.products || [];
        const page = action.meta.arg?.page ?? 1;
        const append = Boolean(action.meta.arg?.append);

        if (append && page > 1) {
          const byId = new Map<string, (typeof incoming)[0]>();
          for (const product of state.searchResults) {
            byId.set(String(product.id), product);
          }
          for (const product of incoming) {
            byId.set(String(product.id), product);
          }
          state.searchResults = Array.from(byId.values());
        } else {
          state.searchResults = incoming;
        }

        state.searchTotal =
          typeof action.payload.total === "number"
            ? action.payload.total
            : state.searchResults.length;
        state.searchPage = page;
        state.error = null;
      })
      .addCase(searchProducts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch Categories
      .addCase(fetchCategories.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesItems = action.payload;
        state.error = null;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch Locations
      .addCase(fetchLocations.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.locations = action.payload;
        state.error = null;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch Product By Slug
      .addCase(fetchProductBySlug.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchProductBySlug.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentProduct = action.payload;
        state.error = null;
      })
      .addCase(fetchProductBySlug.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Fetch Product Reviews
      .addCase(fetchProductReviews.pending, (state) => {
        state.isLoadingReviews = true;
      })
      .addCase(fetchProductReviews.fulfilled, (state, action) => {
        state.isLoadingReviews = false;
        state.productReviews = action.payload;
      })
      .addCase(fetchProductReviews.rejected, (state) => {
        state.isLoadingReviews = false;
      })

      // Post Product Review
      .addCase(postProductReview.fulfilled, (state) => {
        // Reviews will be refetched by the page
      })
      .addCase(postProductReview.rejected, (state, action) => {
        state.error = action.payload;
      })

      // Fetch Seller Products
      .addCase(fetchSellerProducts.pending, (state) => {
        state.isLoadingSellerProducts = true;
      })
      .addCase(fetchSellerProducts.fulfilled, (state, action) => {
        state.isLoadingSellerProducts = false;
        state.sellerProducts = action.payload;
      })
      .addCase(fetchSellerProducts.rejected, (state) => {
        state.isLoadingSellerProducts = false;
      })

      // Fetch Related Products
      .addCase(fetchRelatedProducts.pending, (state) => {
        state.isLoadingRelated = true;
      })
      .addCase(fetchRelatedProducts.fulfilled, (state, action) => {
        state.isLoadingRelated = false;
        state.relatedProducts = action.payload;
      })
      .addCase(fetchRelatedProducts.rejected, (state) => {
        state.isLoadingRelated = false;
      })

      // Fetch My Listings
      .addCase(fetchMyListings.pending, (state) => {
        state.isLoadingMyListings = true;
        state.myListingsError = null;
      })
      .addCase(fetchMyListings.fulfilled, (state, action) => {
        state.isLoadingMyListings = false;
        state.myListingsProducts = action.payload.products;
        state.listings = action.payload.listings;
        state.myListingsPagination = action.payload.pagination;
        state.stats = action.payload.stats;
        state.myListingsError = null;
      })
      .addCase(fetchMyListings.rejected, (state, action) => {
        state.isLoadingMyListings = false;
        state.myListingsError = (action.payload as string) || "Failed to load listings";
      })

      // Fetch Drafts
      .addCase(fetchDrafts.pending, (state) => {
        state.draftsLoading = true;
      })
      .addCase(fetchDrafts.fulfilled, (state, action) => {
        state.draftsLoading = false;
        state.draftListings = action.payload;
        state.draftCount = action.payload.length;
      })
      .addCase(fetchDrafts.rejected, (state) => {
        state.draftsLoading = false;
      });
  },
});

export const {
  clearError,
  setCurrentProduct,
  clearProducts,
  clearSearchResults,
  clearUserProducts,
  clearCurrentProductDetail,
  setDraftListings,
  addDraft,
  removeDraft,
  updateProductInList,
  updateListingInList,
  removeListing,
  setMyListingsPage,
  clearMyListings,
} = productsSlice.actions;

export default productsSlice.reducer;
